/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';

import Loader from 'components/atoms/loader';
import AiManagementAddDocumentPane from 'components/molecules/ai_management_add_document_pane';
import AiManagementAddRequirementPane from 'components/molecules/ai_management_add_requirement_pane';
import AiManagementAnalystStatus from 'components/molecules/ai_management_analyst_status';
import AiManagementDocumentRequirementCard from 'components/molecules/ai_management_document_requirement_card';
import { errorMessageOf, useDocumentRequirements } from 'hooks/useDocumentRequirements';
import { BiddingData } from 'store/modules/bidding_store/types';
import { Container } from './styles';

interface AiManagementBiddingDocumentsProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
}

type Pane = { type: 'requirement' } | { type: 'document'; requirementId: number } | null;

const AiManagementBiddingDocuments: React.FC<AiManagementBiddingDocumentsProps> = ({
  bidding,
  loading,
  error,
}) => {
  const [pane, setPane] = useState<Pane>(null);
  const [analyzeError, setAnalyzeError] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [hideIgnored, setHideIgnored] = useState(false);
  const [reanalyzeError, setReanalyzeError] = useState('');

  const biddingId = bidding ? Number(bidding.bidding_id) || Number(bidding.id) : null;
  const documents = useDocumentRequirements(biddingId);
  const { requirements, progress } = documents;

  useEffect(() => setReanalyzeError(''), [biddingId]);

  const visibleRequirements = hideIgnored
    ? requirements.filter((requirement) => requirement.status !== 'ignored')
    : requirements;
  const paneRequirement =
    pane?.type === 'document'
      ? requirements.find((requirement) => requirement.id === pane.requirementId)
      : undefined;

  const catalog = {
    documents: documents.catalog.documents,
    loading: documents.catalog.loading,
    error: documents.catalog.error,
    onOpen: documents.loadCatalog,
  };

  const handleAnalyze = () => {
    setAnalyzeError('');
    documents
      .analyze()
      .catch((err) =>
        setAnalyzeError(
          errorMessageOf(err, 'Não foi possível buscar as exigências de documentos no edital.'),
        ),
      );
  };

  const handleReanalyze = () => {
    setReanalyzeError('');
    documents
      .reanalyze()
      .catch((err) =>
        setReanalyzeError(errorMessageOf(err, 'Não foi possível iniciar a análise de novo.')),
      );
  };

  const renderIntro = () => (
    <div className="documents-empty card text-center px-4 py-3 mx-auto mt-5">
      <p className="documents-empty-title mb-1">
        Você ainda não listou as exigências de documentos para essa licitação.
      </p>
      <p className="mb-0">
        Use nosso{' '}
        <button
          type="button"
          className="btn btn-link analyst-link p-0 align-baseline"
          onClick={handleAnalyze}
          disabled={documents.analyzing}
        >
          <i className="material-icons-outlined mr-1">smart_toy</i>
          Analista de Habilitação
        </button>{' '}
        para buscar no edital as exigências.
      </p>
      {analyzeError && <div className="alert alert-danger mt-3 mb-0">{analyzeError}</div>}
    </div>
  );

  const renderEmpty = () => (
    <div className="documents-empty card text-center px-4 py-3 mx-auto mt-5">
      <p className="documents-empty-title mb-1">
        Nenhuma exigência de documento foi encontrada no edital.
      </p>
      <p className="mb-0">
        Você pode{' '}
        <button
          type="button"
          className="btn btn-link p-0 align-baseline"
          onClick={() => setPane({ type: 'requirement' })}
        >
          adicionar uma Exigência de Documento
        </button>{' '}
        manualmente.
      </p>
    </div>
  );

  const renderProgress = () => (
    <div className="documents-progress card flex-row flex-wrap align-items-center px-3 py-3 mb-3">
      <div className="flex-grow-1 mr-3">
        <div className="d-flex justify-content-between mb-2">
          <span className="font-weight-bold">
            {progress.confirmed} de {progress.total} documentos revisados
          </span>
          <small className="text-muted">{progress.percentage}%</small>
        </div>
        <div className="progress">
          <div
            className="progress-bar"
            aria-label="Progresso da revisão dos documentos"
            role="progressbar"
            style={{ width: `${progress.percentage}%` }}
            aria-valuenow={progress.percentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>
      <div className="documents-counters d-flex align-items-center pl-3">
        <span className="text-success d-inline-flex align-items-center mr-3">
          <i className="material-icons mr-1">check_circle_outline</i>
          {progress.confirmed} {progress.confirmed === 1 ? 'confirmado' : 'confirmados'}
        </span>
        <span className="text-warning d-inline-flex align-items-center mr-3">
          <i className="material-icons mr-1">error_outline</i>
          {progress.inReview} em revisão
        </span>
        <span className="text-danger d-inline-flex align-items-center">
          <i className="material-icons mr-1">highlight_off</i>
          {progress.pending} {progress.pending === 1 ? 'pendente' : 'pendentes'}
        </span>
      </div>
    </div>
  );

  const renderList = () => (
    <>
      <p className="documents-intro mb-3">
        Identificamos as exigências dos documentos listados abaixo. Você pode adicionar os
        documentos diretamente do seu computador ou importá-los do seu Gerenciar Documentos.
      </p>

      <AiManagementAnalystStatus
        analysis={documents.analysis}
        reanalyzing={documents.reanalyzing}
        reanalyzeError={reanalyzeError}
        onReanalyze={handleReanalyze}
      />

      {renderProgress()}

      <div className="d-flex justify-content-end mb-2">
        <div className="custom-control custom-checkbox">
          <input
            id="hide-ignored-requirements"
            type="checkbox"
            className="custom-control-input"
            checked={hideIgnored}
            onChange={(event) => setHideIgnored(event.target.checked)}
          />
          <label className="custom-control-label" htmlFor="hide-ignored-requirements">
            Esconder documentos ignorados
          </label>
        </div>
      </div>

      {visibleRequirements.map((requirement) => (
        <AiManagementDocumentRequirementCard
          key={requirement.id}
          requirement={requirement}
          open={openId === requirement.id}
          catalog={catalog}
          onToggle={() => setOpenId((prev) => (prev === requirement.id ? null : requirement.id))}
          onAddDocument={() => setPane({ type: 'document', requirementId: requirement.id })}
          onImport={(documentId, options) =>
            documents.importDocument(requirement.id, documentId, options)
          }
          onIgnore={() => documents.ignore(requirement.id)}
          onRestore={() => documents.restore(requirement.id)}
          onRemoveFile={(fileId) => documents.removeFile(requirement.id, fileId)}
        />
      ))}
    </>
  );

  const renderContent = () => {
    if (loading && !bidding) return <Loader />;
    if (error || documents.error) {
      return (
        <div className="alert alert-warning">
          Não foi possível carregar as exigências de documentos do edital.
        </div>
      );
    }
    if (documents.loading) return null;
    if (!documents.analyzed) return renderIntro();
    if (!requirements.length) return renderEmpty();
    return renderList();
  };

  return (
    <Container className="d-flex">
      <div className="documents-main d-flex flex-column flex-grow-1 position-relative">
        <div className="documents-scroll overflow-auto px-4 pt-4 pb-3">
          <div className="d-flex align-items-start justify-content-between mb-1">
            <h4 className="mb-0">Preparar Habilitação</h4>
            <button
              type="button"
              className="btn btn-outline-primary ml-3 flex-shrink-0"
              onClick={() => setPane({ type: 'requirement' })}
            >
              Adicionar Exigência de Documento
            </button>
          </div>
          {documents.analyzed && requirements.length > 0 && (
            <small className="d-block text-muted mb-3">
              Lista de exigências gerada com base nos documentos necessários encontrados no edital.
              Verifique as informações no edital antes de encaminhar para o órgão público.
            </small>
          )}

          {renderContent()}
        </div>

        {documents.analyzing && (
          <div className="documents-searching d-flex align-items-center justify-content-center">
            Procurando as Exigências de Documentos no Edital
          </div>
        )}
      </div>

      {pane && (
        <aside className="documents-add-pane flex-shrink-0 border-left bg-white">
          {pane.type === 'requirement' && (
            <AiManagementAddRequirementPane
              onAdd={(payload) =>
                documents.addRequirement(payload).then((created) => setOpenId(created.id))
              }
              onClose={() => setPane(null)}
            />
          )}
          {pane.type === 'document' && paneRequirement && (
            <AiManagementAddDocumentPane
              key={pane.requirementId}
              requirementTitle={paneRequirement.title}
              onAdd={(payload) => documents.uploadFile(pane.requirementId, payload)}
              onClose={() => setPane(null)}
            />
          )}
        </aside>
      )}
    </Container>
  );
};

export default AiManagementBiddingDocuments;
